import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Stack, Box, Typography, TextField, MenuItem, Button, Chip, Divider, Alert } from "@mui/material";
import PsychologistApi, { ProviderDoc } from "../../api/PsychologistApi";
import { DOC_KIND_KEYS, fileToBase64 } from "./profileShared";

/** Bewijsstukken (diploma/erkenning/visum/…): zelfstandige sectie die zijn
 *  eigen documentlijst beheert. `onCountChange` laat de onboarding-wizard de
 *  volgende-stap-knop gaten op minstens één upload. */
export default function DocumentsSection({ onCountChange }: { onCountChange?: (n: number) => void }) {
  const { t } = useTranslation();
  const [docs, setDocs] = useState<ProviderDoc[]>([]);
  const [docKind, setDocKind] = useState<string>("diploma");
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");

  const refresh = async () => {
    const d = await PsychologistApi.listDocuments();
    setDocs(d);
    onCountChange?.(d.length);
  };
  useEffect(() => { refresh().catch(() => {}); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onUpload = async (file?: File | null) => {
    if (!file) return;
    setErr("");
    setUploading(true);
    try {
      await PsychologistApi.uploadDocument({ kind: docKind, filename: file.name, contentType: file.type, dataBase64: await fileToBase64(file) });
      await refresh();
    } catch (e: any) {
      setErr(e?.response?.data?.error || t("vetting.uploadError"));
    } finally {
      setUploading(false);
    }
  };
  const deleteDoc = async (id: string) => { await PsychologistApi.deleteDocument(id); await refresh(); };

  return (
    <Box>
      {docs.length > 0 && (
        <Stack divider={<Divider />} spacing={0} sx={{ mb: 2 }}>
          {docs.map((d) => (
            <Box key={d.id} sx={{ display: "flex", alignItems: "center", gap: 1.5, py: 1 }}>
              <Chip size="small" label={t(`vetting.docKinds.${d.kind}`)} sx={{ bgcolor: "var(--primary-50)", color: "primary.dark" }} />
              <Typography sx={{ fontSize: ".9rem", flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.filename}</Typography>
              <Button size="small" color="error" onClick={() => deleteDoc(d.id)}>{t("vetting.deleteDoc")}</Button>
            </Box>
          ))}
        </Stack>
      )}
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ alignItems: { sm: "center" } }}>
        <TextField select size="small" label={t("vetting.docKind")} value={docKind} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDocKind(e.target.value)} sx={{ minWidth: 200 }}>
          {DOC_KIND_KEYS.map((k) => <MenuItem key={k} value={k}>{t(`vetting.docKinds.${k}`)}</MenuItem>)}
        </TextField>
        <Button component="label" variant="outlined" disabled={uploading} sx={{ alignSelf: "flex-start" }}>
          {uploading ? t("vetting.uploading") : t("vetting.uploadDoc")}
          <input hidden type="file" accept="application/pdf,image/png,image/jpeg" onChange={(e: React.ChangeEvent<HTMLInputElement>) => { onUpload(e.target.files?.[0]); e.target.value = ""; }} />
        </Button>
      </Stack>
      {err && <Alert severity="error" sx={{ mt: 2 }} onClose={() => setErr("")}>{err}</Alert>}
    </Box>
  );
}
